import type { ReactNode } from 'react'
import type { CreatorPlatform, OutreachAngle, OutreachTone } from '@/api/client'
import { ANGLES, TONES, checkTemplate } from '@/lib/creators'
import { cn } from '@/lib/utils'
import { Field, Input, Segmented, Textarea } from '@/ui'

/** A labelled on/off switch with a line of help under it. */
export function SwitchRow({
  label,
  hint,
  on,
  onChange,
}: {
  label: string
  hint?: ReactNode
  on: boolean
  onChange: (on: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-muted/40 p-3">
      <div className="min-w-0">
        <div className="text-body font-medium">{label}</div>
        {hint && <p className="mt-0.5 text-meta text-muted-foreground">{hint}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={on}
        aria-label={label}
        onClick={() => onChange(!on)}
        className={cn(
          'relative mt-0.5 h-5 w-9 shrink-0 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40',
          on ? 'bg-status-published' : 'bg-border',
        )}
      >
        <span className={cn('absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform', on && 'translate-x-4')} />
      </button>
    </div>
  )
}

export interface GroupFieldsValue {
  offer: string
  angle: OutreachAngle
  tone: OutreachTone
  template: string
  personalize: boolean
}

/** The message approach of one group: what is offered, the angle and tone,
 *  the template, and whether each message is written for the person. Shared
 *  by the new-outreach step 3 and the group drawer. */
export function GroupFields({
  value,
  onChange,
  language,
  platform,
  idPrefix,
  showProblems = true,
}: {
  value: GroupFieldsValue
  onChange: (patch: Partial<GroupFieldsValue>) => void
  language: string
  platform?: CreatorPlatform
  idPrefix: string
  showProblems?: boolean
}) {
  const check = checkTemplate(value.template, language, platform)
  return (
    <div className="space-y-3">
      <Field label="제안하는 것" hint="예: 1년 Pro 무료" required>
        <Input value={value.offer} onChange={(e) => onChange({ offer: e.target.value })} aria-label={`${idPrefix} 제안`} />
      </Field>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="min-w-0 space-y-1.5">
          <div className="text-meta font-medium">접근</div>
          <Segmented label={`${idPrefix} 접근`} value={value.angle} onChange={(angle) => onChange({ angle })} options={ANGLES} />
        </div>
        <div className="min-w-0 space-y-1.5">
          <div className="text-meta font-medium">말투</div>
          <Segmented label={`${idPrefix} 말투`} value={value.tone} onChange={(tone) => onChange({ tone })} options={TONES} />
        </div>
      </div>
      <Field
        label="템플릿"
        required
        aside={<span className={cn('tabular-nums', check.length > 1000 && 'text-status-failed')}>{check.length} / 1000자</span>}
        hint={
          <>
            <code>{'{{link}}'}</code>는 정확히 한 번 넣는다. <code>{'{{name}}'}</code>은 받는 사람 이름으로 바뀌고, 없어도 된다. 마지막 줄의 거절 안내는
            시스템이 붙이니 적지 않는다.
          </>
        }
        error={showProblems && check.problems.length > 0 ? check.problems.join(' ') : undefined}
      >
        <Textarea rows={6} value={value.template} onChange={(e) => onChange({ template: e.target.value })} aria-label={`${idPrefix} 템플릿`} />
      </Field>
      <SwitchRow
        label="사람마다 맞춤"
        on={value.personalize}
        onChange={(personalize) => onChange({ personalize })}
        hint={
          value.personalize
            ? '템플릿을 바탕으로 사람마다 그 사람의 글에서 하나를 짚어 새로 쓴다. 쓰지 못하면 템플릿을 그대로 채운다.'
            : '템플릿을 그대로 쓰고 이름만 채운다.'
        }
      />
    </div>
  )
}
